import { StandardEvents } from '@shopify/events';

const COLORS = ['White Gold', 'Yellow Gold', 'Rose Gold'];
const KARATS = ['14K', '18K'];
const SIZES = { us: [], uk: [], europe: [] };
const SYSTEMS = { us: 'USA/Canada', uk: 'UK', europe: 'Europe' };
// Labels are accepted only when attached to an exact supplier-verified variant below.
function verifiedSizeLabel(system, value) {
  if (typeof value !== 'string') return false;
  if (system === 'us') {
    const match = value.match(/^(\d{1,2}(?:\.(?:25|5|75))?)(?: \((?:standard|exact)\))?$/);
    return Boolean(match && Number(match[1]) > 0);
  }
  if (system === 'uk') return /^[A-Z](?:½| 1\/2)?$/.test(value);
  if (system === 'europe') return /^\d{2}(?:\.5)?$/.test(value);
  return false;
}


export function variantKey(id) {
  const match = String(id ?? '').match(/^(?:gid:\/\/shopify\/ProductVariant\/)?([1-9]\d*)$/);
  return match?.[1] ?? '';
}

export function nativeMaterialMatches(options, color, karat) {
  for (const value of options ?? []) {
    const nativeColor = /\b(white|yellow|rose|pink)\s+gold\b/i.exec(String(value))?.[1]?.toLowerCase();
    const nativeKarat = /\b(14|18)\s*(?:k|kt|karat)\b/i.exec(String(value))?.[1];
    if (nativeColor && `${nativeColor === 'pink' ? 'Rose' : nativeColor[0].toUpperCase() + nativeColor.slice(1)} Gold` !== color) return false;
    if (nativeKarat && `${nativeKarat}K` !== karat) return false;
    if (/\b(?:platinum|silver|palladium)\b/i.test(String(value)) && color !== null) return false;
  }
  return true;
}

export function verifiedEntries(metadata, variants, isRing, currencyCode = 'USD') {
  if (currencyCode !== 'USD' || !metadata || typeof metadata !== 'object' || Array.isArray(metadata) || metadata.schema !== 1 || typeof metadata.verifiedAt !== 'string' ||
      !/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z)?$/.test(metadata.verifiedAt) || !Number.isFinite(Date.parse(metadata.verifiedAt)) ||
      new Date(metadata.verifiedAt).toISOString().slice(0, 10) !== metadata.verifiedAt.slice(0, 10) || !Array.isArray(metadata.variants) || metadata.variants.length > 2048) return [];
  const actual = new Map(variants.map((v) => [variantKey(v.id), v]));
  const seen = new Set();
  const rows = [];
  for (const entry of metadata.variants) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry) || typeof entry.id !== 'string' || !/^gid:\/\/shopify\/ProductVariant\/[1-9]\d*$/.test(entry.id)) return [];
    const id = variantKey(entry?.id);
    const native = actual.get(id);
    if (!native || seen.has(id) || entry.orderable !== true || !entry.sizes || typeof entry.sizes !== 'object' || Array.isArray(entry.sizes) ||
        entry.leadTime !== undefined && (typeof entry.leadTime !== 'string' || entry.leadTime.length > 240)) return [];
    seen.add(id);
    const { color, karat } = entry;
    if (!nativeMaterialMatches(native.options, color, karat)) return [];
    if (color !== null && !COLORS.includes(color) || karat !== null && !KARATS.includes(karat) || (color === null) !== (karat === null)) return [];
    const sizes = {};
    for (const system of Object.keys(SIZES)) {
      const size = entry.sizes?.[system];
      if (!(size === null || typeof size === 'string')) return [];
      if (verifiedSizeLabel(system, size)) sizes[system] = size;
    }
    rows.push({ id, color, karat, sizes, native });
  }
  return rows.filter((row) => row.native.available === true && Number.isSafeInteger(row.native.priceCents) && row.native.priceCents > 0 && (!isRing || Object.keys(row.sizes).length));
}

export function managedOption(name, values, isRing, verifiedGold = false) {
  const key = String(name).toLowerCase().replace(/[^a-z]/g, '');
  if (isRing && /^(?:(?:ring|finger)(?:size|sizes|sizing)|(?:us|usa|uscanada|usacanada|canada|uk|eu|europe|european)?size)$/.test(key)) return true;
  if (/^(?:metal|material|metaltype|goldtype|goldquality|metalquality|gold|goldcolor|goldcolour|metalcolor|metalcolour|goldkarat|goldcarat|goldpurity|metalpurity|karat|purity)$/.test(key)) return true;
  if (key === 'carat' && values.length > 0 && values.every((v) => /^(?:14|18)\s*(?:k|kt|karat|carat)$/i.test(String(v)))) return true;
  return key === 'color' && values.length > 0 && (verifiedGold || values.some((v) => /gold|platinum/i.test(String(v)))) && values.every((v) => /^(?:(?:14|18)\s*[kK]\s*)?(?:white|yellow|rose|pink)(?:\s+gold)?$|^platinum$/i.test(String(v)));
}

export function entryProperties(entry, system, size, isRing) {
  if (!nativeMaterialMatches(entry.native?.options, entry.color, entry.karat)) return null;
  const properties = {};
  if (entry.color) properties['Gold color'] = entry.color;
  if (entry.karat) properties['Gold karat'] = entry.karat;
  if (isRing) {
    if (!SYSTEMS[system] || !size || entry.sizes[system] !== size) return null;
    properties['Ring sizing system'] = SYSTEMS[system];
    properties['Ring size'] = size;
    const canonical = entry.sizes.us ? 'us' : Object.keys(SYSTEMS).find((s) => entry.sizes[s]);
    if (!canonical) return null;
    properties['Supplier ring size'] = `${canonical === 'us' ? 'US' : SYSTEMS[canonical]} ${entry.sizes[canonical]}`;
  }
  return properties;
}

class VerifiedJewelryOptions extends (globalThis.HTMLElement ?? class {}) {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    try {
      this.data = JSON.parse(this.querySelector('[data-ms-options-data]').textContent);
    } catch {
      return;
    }
    this.isRing = this.dataset.ring === 'true';
    this.form = this.closest('form');
    this.formComponent = this.closest('product-form-component');
    this.section = this.closest('.shopify-section, dialog, product-card') ?? this.parentElement;
    this.controls = Object.fromEntries(Array.from(this.querySelectorAll('[data-ms-select]')).map((el) => [el.dataset.msSelect, el]));
    this.status = this.querySelector('[data-ms-options-status]');
    this.selection = { material: '', color: '', karat: '', system: 'us', size: '' };
    this.abort = new AbortController();
    this.rows = verifiedEntries(this.data.verifiedOptions, this.data.variants, this.isRing, this.data.currencyCode);
    this.managed = this.data.optionNames.map((name, i) => managedOption(name, this.data.variants.map((v) => v.options[i]), this.isRing, this.rows.some((r) => r.color)));
    this.hideManagedNativeOptions();
    this.addEventListener('change', this.onChange.bind(this), { signal: this.abort.signal });
    this.section.addEventListener(StandardEvents.productSelect, this.onNativeChange.bind(this), { signal: this.abort.signal });
    const materials = this.unique(this.rows.map((row) => this.materialKey(row)));
    if (materials.length === 1) this.selection.material = materials[0];
    const rows = this.rows.filter((row) => this.materialKey(row) === this.selection.material);
    const colors = this.unique(rows.map((row) => row.color).filter(Boolean));
    const karats = this.unique(rows.map((row) => row.karat).filter(Boolean));
    if (colors.length === 1) this.selection.color = colors[0];
    if (karats.length === 1) this.selection.karat = karats[0];
    this.refresh(false);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.initialized = false;
  }

  unique(values) {
    return [...new Set(values)];
  }

  currentVariant() {
    const id = variantKey(this.form?.querySelector('input[name="id"]')?.value);
    return this.data.variants.find((v) => variantKey(v.id) === id);
  }

  materialKey(row) {
    if (row.color) return 'gold';
    const values = row.native.options.filter((_, i) => this.managed[i] && !/size|sizing/i.test(this.data.optionNames[i]));
    return `other:${values.join(' / ') || this.data.productTitle}`;
  }

  dimensionRows() {
    const current = this.currentVariant();
    if (!current) return this.form ? [] : this.rows;
    return this.rows.filter((row) => row.native.options.every((value, i) => this.managed[i] || value === current.options[i]));
  }

  hideManagedNativeOptions() {
    const picker = this.section.querySelector(`variant-picker[data-product-id="${this.data.productId}"]`);
    if (!picker) return;
    const names = this.data.optionNames.filter((_, i) => this.managed[i]);
    for (const wrapper of picker.querySelectorAll('.variant-option')) {
      const input = wrapper.querySelector('[data-option-name]');
      const label = wrapper.querySelector('legend, label')?.textContent?.trim();
      const single = wrapper.dataset.testid === 'variant-option-single' && names.some((name) => wrapper.textContent.trim().startsWith(name));
      if (names.includes(input?.dataset.optionName) || names.includes(label) || single) {
        wrapper.hidden = true;
        wrapper.style.display = 'none';
      }
    }
  }

  optionList(control, values, selected, labels = {}) {
    if (!control) return;
    control.replaceChildren();
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.data.labels.choose;
    control.append(placeholder);
    for (const value of values) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = labels[value] ?? value;
      control.append(option);
    }
    control.value = values.includes(selected) ? selected : '';
    control.disabled = !values.length;
  }

  matchingRows(rows = this.dimensionRows(), exclude = '') {
    const s = this.selection;
    return rows.filter((row) => {
      if (exclude !== 'material' && s.material && this.materialKey(row) !== s.material) return false;
      if (row.color && exclude !== 'color' && s.color && row.color !== s.color) return false;
      if (row.karat && exclude !== 'karat' && s.karat && row.karat !== s.karat) return false;
      if (this.isRing && exclude !== 'size' && s.size && row.sizes[s.system] !== s.size) return false;
      return true;
    });
  }

  onChange(event) {
    const field = event.target?.dataset?.msSelect;
    if (!field) return;
    event.stopPropagation();
    if (this.pending) {
      this.refresh(false);
      return;
    }
    const previous = this.chosen;
    this.selection[field] = event.target.value;
    this.failure = false;
    if (field === 'system') {
      const equivalent = previous?.sizes[this.selection.system];
      this.selection.size = equivalent && verifiedSizeLabel(this.selection.system, equivalent) ? equivalent : '';
    } else if (field === 'material') {
      this.selection.color = '';
      this.selection.karat = '';
      this.selection.size = '';
      const rows = this.matchingRows(this.dimensionRows());
      const colors = this.unique(rows.map((r) => r.color).filter(Boolean));
      const karats = this.unique(rows.map((r) => r.karat).filter(Boolean));
      if (colors.length === 1) this.selection.color = colors[0];
      if (karats.length === 1) this.selection.karat = karats[0];
    } else if (field === 'color' || field === 'karat') {
      const other = field === 'color' ? 'karat' : 'color';
      if (!this.matchingRows().length) this.selection[other] = '';
      if (!this.matchingRows().length) this.selection.size = '';
    }
    this.refresh(true);
  }

  onNativeChange(event) {
    if (String(event.product?.id ?? event.detail?.product?.id ?? event.target?.dataset?.productId ?? '') !== String(this.data.productId)) return;
    const generation = (this.nativeGeneration ?? 0) + 1;
    this.nativeGeneration = generation;
    this.failure = false;
    this.pending = true;
    this.syncPurchase(false);
    const promise = event.promise ?? event.detail?.promise;
    if (!promise?.then) return;
    promise.then((result) => {
      if (!this.isConnected || generation !== this.nativeGeneration) return;
      const detail = result?.detail;
      const replacement = detail?.html?.querySelector(`product-form-component[data-product-id="${this.data.productId}"]`);
      if (this.formComponent) this.formComponent.dataset.msBaseEligible = replacement?.dataset.msBaseEligible ?? 'false';
      try {
        const fresh = JSON.parse(replacement?.querySelector('[data-ms-options-data]')?.textContent ?? 'null');
        if (String(fresh?.productId) === String(this.data.productId)) {
          this.data = fresh;
          this.rows = verifiedEntries(fresh.verifiedOptions, fresh.variants, this.isRing, fresh.currencyCode);
        }
      } catch {
        this.rows = [];
      }
      this.pending = false;
      requestAnimationFrame(() => {
        this.hideManagedNativeOptions();
        this.refresh(false);
      });
    }).catch(() => {
      if (!this.isConnected || generation !== this.nativeGeneration) return;
      this.pending = false;
      this.failure = true;
      this.refresh(false);
    });
  }

  refresh(resolve) {
    const s = this.selection;
    const base = this.dimensionRows();
    const materials = this.unique(base.map((r) => this.materialKey(r)));
    if (!materials.includes(s.material)) s.material = materials.length === 1 ? materials[0] : '';
    const materialRows = base.filter((r) => this.materialKey(r) === s.material);
    const isGold = s.material === 'gold';
    if (!isGold) {
      s.color = '';
      s.karat = '';
    }
    this.querySelector('[data-ms-field="material"]').hidden = materials.length < 2 && !materials.some((m) => m.startsWith('other:'));
    this.querySelector('[data-ms-field="color"]').hidden = !isGold;
    this.querySelector('[data-ms-field="karat"]').hidden = !isGold;
    const materialLabels = Object.fromEntries(materials.map((m) => [m, m === 'gold' ? this.data.labels.gold : m.slice(6)]));
    this.optionList(this.controls.material, materials, s.material, materialLabels);
    const colors = COLORS.filter((color) => materialRows.some((r) => r.color === color && (!s.karat || r.karat === s.karat)));
    const karats = KARATS.filter((karat) => materialRows.some((r) => r.karat === karat && (!s.color || r.color === s.color)));
    if (s.color && !colors.includes(s.color)) s.color = '';
    if (s.karat && !karats.includes(s.karat)) s.karat = '';
    this.optionList(this.controls.color, colors, s.color);
    this.optionList(this.controls.karat, karats, s.karat);
    if (this.isRing) {
      const rows = this.matchingRows(base, 'size');
      const sizes = [...new Set(rows.map((r) => r.sizes[s.system]).filter((size) => verifiedSizeLabel(s.system, size)))].sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
      if (!sizes.includes(s.size)) s.size = '';
      this.controls.system.disabled = !base.length;
      this.controls.system.value = s.system;
      this.optionList(this.controls.size, sizes, s.size);
    }
    const complete = !!s.material && (!isGold || !!s.color && !!s.karat) && (!this.isRing || !!s.size);
    const matches = complete ? this.matchingRows(base) : [];
    const currentId = variantKey(this.currentVariant()?.id);
    const exact = matches.filter((r) => r.id === currentId);
    const possible = exact.length ? exact : matches;
    this.chosen = possible.length && this.unique(possible.map((r) => r.id)).length === 1 ? possible[0] : null;
    if (resolve && this.chosen && this.chosen.id !== currentId && !this.pending) this.resolveVariant(this.chosen.id);
    const ready = !this.pending && !this.failure && !!this.chosen && this.chosen.id === currentId && this.formComponent?.dataset.msBaseEligible === 'true';
    this.ready = ready;
    this.writeProperties(ready ? this.chosen : null);
    this.syncPurchase(ready);
    this.status.textContent = this.pending ? this.data.labels.loading : this.failure ? this.data.labels.unavailable : !base.length ? this.data.labels.pending : !complete ? this.data.labels.required : !this.chosen || !ready && this.form ? this.data.labels.unavailable : '';
  }

  resolveVariant(id) {
    const picker = this.section.querySelector(`variant-picker[data-product-id="${this.data.productId}"]`);
    if (typeof picker?.fetchUpdatedSection !== 'function') {
      this.failure = true;
      return;
    }
    this.pending = true;
    this.syncPurchase(false);
    const url = new URL(this.data.productUrl, window.location.origin);
    url.searchParams.set('variant', id);
    const theme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (theme) url.searchParams.set('preview_theme_id', theme);
    picker.fetchUpdatedSection(`${url.pathname}${url.search}`, { detail: { variantId: id } });
    if (!this.closest('quick-add-dialog, product-card')) {
      const current = new URL(window.location.href);
      current.searchParams.set('variant', id);
      history.replaceState({}, '', current);
    }
  }

  writeProperties(entry) {
    if (!this.form) return;
    for (const input of this.form.querySelectorAll('[data-ms-configuration-property]')) input.remove();
    if (!entry) return;
    const properties = entryProperties(entry, this.selection.system, this.selection.size, this.isRing);
    if (!properties) return;
    for (const [name, value] of Object.entries(properties)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.dataset.msConfigurationProperty = '';
      this.form.append(input);
    }
  }

  syncPurchase(ready) {
    if (this.pending) for (const select of Object.values(this.controls)) select.disabled = true;
    if (!this.form) return;
    for (const button of this.form.querySelectorAll('button[type="submit"], button[name="add"]')) button.disabled = !ready;
    for (const accelerated of this.form.querySelectorAll('[data-ms-accelerated], [ref="acceleratedCheckoutButtonContainer"]')) accelerated.hidden = !ready;
    const sticky = this.section.querySelector('sticky-add-to-cart button');
    if (sticky) sticky.disabled = !ready;
  }

  validate() {
    this.refresh(false);
    if (!this.ready || !this.chosen || this.chosen.id !== variantKey(this.form?.querySelector('input[name="id"]')?.value)) {
      this.status.textContent = this.rows.length ? this.data.labels.required : this.data.labels.pending;
      const missing = Object.values(this.controls).find((select) => !select.disabled && !select.value && !select.closest('[hidden]'));
      missing?.focus();
      return false;
    }
    return true;
  }
}

if (globalThis.customElements && !customElements.get('ms-verified-jewelry-options')) customElements.define('ms-verified-jewelry-options', VerifiedJewelryOptions);
